import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGrn } from "@/features/grns/api";
import { useCreateRtv, useReturnable, useRtvOptions } from "@/features/vendor-returns/api";
import { KindPill } from "@/features/vendor-returns/bits";

export const Route = createFileRoute("/_authenticated/vendor-returns/new")({
  validateSearch: (s: Record<string, unknown>): { grnId?: string } => ({ grnId: typeof s.grnId === "string" ? s.grnId : undefined }),
  component: NewRtv,
});

function NewRtv() {
  const { grnId } = Route.useSearch();
  const navigate = useNavigate();
  const { data: grn } = useGrn(grnId);
  const { data: lines = [], isLoading } = useReturnable(grnId);
  const { data: opts } = useRtvOptions();
  const create = useCreateRtv();
  const [qty, setQty] = useState<Record<string, string>>({});          // key = `${grn_item_id}:${source}`
  const [lineRemarks, setLineRemarks] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [resolution, setResolution] = useState("");
  const [vendorRef, setVendorRef] = useState("");

  if (!grnId) {
    return <Alert><AlertTitle>Choose a GRN</AlertTitle><AlertDescription>Open a GRN and use "Return to Vendor", or pick one from the <Link to="/grns" className="underline">GRN list</Link>.</AlertDescription></Alert>;
  }

  const rows = lines.flatMap((l) => (["defective", "rejected", "packaging"] as const)
    .filter((src) => Number(l[src].quantity) > 0 || (src === "packaging" && (Number(l.packaging.pending_inspection) > 0 || Number(l.packaging.awaiting_decision) > 0)))
    .map((src) => ({ key: `${l.grn_item_id}:${src}`, line: l, src, info: l[src] })));
  const picked = rows.filter((r) => Number(qty[r.key]) > 0);
  const overBalance = picked.some((r) => Number(qty[r.key]) > Number(r.info.balance));
  const missingLineRemarks = picked.some((r) => !(lineRemarks[r.key] ?? "").trim());
  const configuredReasons = opts?.reasons ?? [];
  const canSubmit = !create.isPending && picked.length > 0 && !overBalance && !missingLineRemarks && reason.trim() && remarks.trim() && opts?.can_process;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      const rtv = await create.mutateAsync({
        grn_id: grnId, reason: reason.trim(), remarks: remarks.trim(), resolution: resolution || undefined, vendor_reference: vendorRef || undefined,
        items: picked.map((r) => ({ grn_item_id: r.line.grn_item_id, source: r.src, quantity: Number(qty[r.key]), remarks: (lineRemarks[r.key] ?? "").trim() })),
      });
      toast.success(`${rtv.rtv_number} saved as Draft`);
      navigate({ to: "/vendor-returns/$rtvId", params: { rtvId: rtv.id } });
    } catch (e) { toast.error((e as Error).message); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/grns/$grnId", params: { grnId } })}><ArrowLeft className="mr-1 h-4 w-4" />Back to GRN</Button>
      {opts && !opts.can_process && (
        <Alert variant="destructive"><AlertTitle>Not authorised</AlertTitle><AlertDescription>You do not have permission to process vendor returns. Ask an administrator to assign it to your role.</AlertDescription></Alert>
      )}
      <Card>
        <CardHeader><CardTitle className="text-base">New Return to Vendor — GRN {grn?.grn_no ?? ""}{grn?.vendor?.name ? ` · ${grn.vendor.name}` : ""}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">Defective, rejected and inspected package-damaged quantities can be returned. Nothing moves in stock until the return is dispatched.</p>
          <Table>
            <TableHeader><TableRow>
              <TableHead>#</TableHead><TableHead>Item</TableHead><TableHead>Source</TableHead>
              <TableHead className="text-right">On GRN</TableHead><TableHead className="text-right">Already returned</TableHead>
              <TableHead className="text-right">Returnable</TableHead><TableHead className="w-28 text-right">Return qty</TableHead><TableHead>Remarks *</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
              {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">This GRN has no defective or rejected quantities.</TableCell></TableRow>}
              {rows.map((r) => {
                const balance = Number(r.info.balance);
                const bad = Number(qty[r.key]) > balance;
                return (
                  <TableRow key={r.key}>
                    <TableCell className="text-xs">{r.line.line_no}</TableCell>
                    <TableCell className="text-sm">{r.line.description} <KindPill kind={r.line.item_kind} /></TableCell>
                    <TableCell className="text-xs capitalize">
                      {r.src === "packaging" ? "Packaging damage" : r.src}
                      {r.src === "packaging" && (
                        <div className="text-[11px] normal-case text-muted-foreground">
                          {Number(r.line.packaging.pending_inspection) + Number(r.line.packaging.awaiting_decision) > 0 && <>Pending inspection/decision: {Number(r.line.packaging.pending_inspection) + Number(r.line.packaging.awaiting_decision)} (not returnable yet). </>}
                          {r.line.packaging.inspections.map((i) => <span key={i.reference}>{i.reference}: {Number(i.return_qty)} for return. </span>)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{Number(r.info.quantity)}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(r.info.returned)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{balance}</TableCell>
                    <TableCell>
                      <Input type="number" min={0} max={balance} step="any" disabled={balance <= 0} className={`h-8 text-right ${bad ? "border-red-500" : ""}`}
                        value={qty[r.key] ?? ""} onChange={(e) => setQty((s) => ({ ...s, [r.key]: e.target.value }))} />
                    </TableCell>
                    <TableCell><Input className="h-8" disabled={balance <= 0} placeholder="Why is this returned?" value={lineRemarks[r.key] ?? ""}
                      onChange={(e) => setLineRemarks((s) => ({ ...s, [r.key]: e.target.value }))} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {overBalance && <p className="text-xs text-red-600">A quantity is above what can still be returned.</p>}

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Return reason *</Label>
              {configuredReasons.length > 0 ? (
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>{configuredReasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for return" />}
            </div>
            <div className="space-y-1">
              <Label>Vendor reference (RMA / challan)</Label>
              <Input value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} />
            </div>
            {(opts?.resolutions.length ?? 0) > 0 && (
              <div className="space-y-1">
                <Label>Resolution</Label>
                <Select value={resolution} onValueChange={setResolution}>
                  <SelectTrigger><SelectValue placeholder="Select resolution (optional)" /></SelectTrigger>
                  <SelectContent>{opts!.resolutions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1 md:col-span-2">
              <Label>Remarks *</Label>
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={!canSubmit}>{create.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save as Draft</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
